import type { EvalSuiteSpec } from '@sbc/domain-models/agents';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/client';
import {
  useDeleteEvalSuite,
  useDeleteTask,
  useEvalSuite,
  useUpdateEvalSuite,
} from '@/lib/api/eval-suites';

import { RunBox } from './run-box';
import { SuiteForm } from './suite-form';

export function SuiteDetailPage({ suiteId }: { suiteId: string }) {
  const navigate = useNavigate();
  const suiteQuery = useEvalSuite(suiteId);
  const updateSuite = useUpdateEvalSuite(suiteId);
  const deleteSuite = useDeleteEvalSuite();
  const deleteTask = useDeleteTask(suiteId);
  const [error, setError] = useState<string | null>(null);

  if (suiteQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (suiteQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load suite: {suiteQuery.error.message}
      </p>
    );
  }

  const suite = suiteQuery.data;
  const initial: EvalSuiteSpec = {
    name: suite.name,
    description: suite.description ?? '',
    agentId: suite.agentId,
  };

  const onSubmit = (spec: EvalSuiteSpec) => {
    setError(null);
    updateSuite.mutate(spec, {
      onError: (caught) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not save the suite.'),
    });
  };

  const onDelete = () => {
    setError(null);
    deleteSuite.mutate(suiteId, {
      onSuccess: () => navigate({ to: '/evals/suites' }),
      onError: (caught) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not delete the suite.'),
    });
  };

  const onDeleteTask = (taskId: string) => {
    setError(null);
    deleteTask.mutate(taskId, {
      onError: (caught) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not delete the task.'),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{suite.name}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {suite.tasks.length} task(s)
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/evals/suites">Back</Link>
        </Button>
      </div>

      <RunBox suiteId={suiteId} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Agent configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <SuiteForm
            initial={initial}
            submitting={updateSuite.isPending}
            submitLabel="Save changes"
            error={error}
            onSubmit={onSubmit}
            onCancel={() => navigate({ to: '/evals/suites' })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="text-base">Tasks</CardTitle>
          <Button size="sm" asChild>
            <Link to="/evals/suites/$suiteId/tasks/new" params={{ suiteId }}>
              Add task
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead>Trials</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {suite.tasks.length ? (
                  suite.tasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell className="font-medium">
                        <Link
                          to="/evals/suites/$suiteId/tasks/$taskId"
                          params={{ suiteId, taskId: task.id }}
                          className="hover:underline"
                        >
                          {task.taskKey}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{task.suite}</Badge>
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {task.passThreshold}/{task.trials}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteTask(task.id)}
                          disabled={deleteTask.isPending}
                          className="text-[var(--color-destructive)]"
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center">
                      No tasks yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Button
        variant="outline"
        onClick={onDelete}
        disabled={deleteSuite.isPending}
        className="text-[var(--color-destructive)]"
      >
        {deleteSuite.isPending ? 'Deleting…' : 'Delete suite'}
      </Button>
    </div>
  );
}
